import { useAuthStore, parseJwtClaims } from '@/stores/authStore'
import { authApi } from '@/api/clients/authApi'
import { isEmbedded } from '@/lib/embed'
import { requestTokenFromParent, markEmbedSessionLost } from '@/lib/embedBridge'
import type {
  ApiResponse,
  ScannerInfo,
  Source,
  CreateSourceRequest,
  UpdateSourceRequest,
  SourceCredential,
  SetCredentialRequest,
  ScanJob,
  CreateScanRequest,
  BomImportRequest,
  BomImportResult,
  FindingsReport,
  PackageFindingsReport,
  BOMFindingsReport,
  ListSourcesParams,
  ListScansParams,
  ListFindingsParams,
  UploadSessionResponse,
  SbomComponentsResponse,
  SbomVersionsResponse,
  CbomVersionsResponse,
  InventoryCompositeSbomVersionsResponse,
  InventoryCompositeCbomVersionsResponse,
  ListSbomComponentsParams,
  ListPackageFindingsParams,
  Inventory,
  InventoryDetail,
  InventoryRisk,
  SourceRiskDetail,
  InventoriesPage,
  AddSourcesResult,
  CreateInventoryRequest,
  UpdateInventoryRequest,
  AddSourcesRequest,
  RiskHistory,
  CmdbHandler,
  CreateHandlerRequest,
  UpdateHandlerRequest,
  CmdbHealthResult,
  CmdbFieldMapping,
  CmdbSyncStatusPage,
  CmdbRiskSummary,
  CmdbMatchSuggestion,
  ConfirmMatchesRequest,
  CmdbImportResult,
  CmdbMappingProfile,
  ProfileDirection,
  CreateCmdbProfileRequest,
  UpdateCmdbProfileRequest,
  ListSyncStatusParams,
  FileFormat,
  CmdbSetCredentialRequest,
  CredentialHintResponse,
  AdapterTemplate,
  FieldDiscoveryResult,
  CmdbProvisionGuide,
  Credential,
  CreateCredentialRequest,
  UpdateCredentialRequest,
} from '@/api/types'

// ── Upload offset mismatch error ──────────────────────────────────────────────

export class UploadOffsetMismatchError extends Error {
  public serverOffset: number
  constructor(serverOffset: number) {
    super('Upload offset mismatch')
    this.name = 'UploadOffsetMismatchError'
    this.serverOffset = serverOffset
  }
}

// A proxy in front of the API rejected a chunk as too large (HTTP 413).
// Retrying the same chunk cannot succeed.
export class UploadChunkTooLargeError extends Error {
  public chunkBytes: number
  constructor(chunkBytes: number) {
    super('Upload chunk rejected as too large (413)')
    this.name = 'UploadChunkTooLargeError'
    this.chunkBytes = chunkBytes
  }
}

// In development: relative paths → Vite dev proxy → backend.
// In production: set VITE_BASE_URL to the backend origin (e.g. https://api.company.com)
// when the frontend and API are on different origins. Leave empty for same-origin deployments.
const BASE = import.meta.env.VITE_BASE_URL ?? ''

// ── Token refresh mutex ───────────────────────────────────────────────────────
// Prevents multiple concurrent 401 responses from each triggering their own
// refresh request. All callers share the same in-flight promise.

let refreshPromise: Promise<string> | null = null

async function getFreshToken(): Promise<string> {
  // Embedded: the partner parent owns refreshing — ask it, never redirect the frame to /login.
  if (isEmbedded()) {
    if (!refreshPromise) {
      refreshPromise = requestTokenFromParent()
        .catch((err) => {
          markEmbedSessionLost()
          throw err
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  const store = useAuthStore.getState()
  if (!store.refreshToken) {
    store.clearSession()
    window.location.href = `${import.meta.env.BASE_URL}login`
    throw new Error('No refresh token available')
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const data = await authApi.refresh(store.refreshToken!)
      const claims = parseJwtClaims(data.access_token)
      store.updateTokens(data.access_token, data.refresh_token, data.expires_at)
      store.setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        userId: claims.uid,
        orgId: claims.oid,
        role: claims.role,
      })
      return data.access_token
    })()
      .catch((err) => {
        store.clearSession()
        window.location.href = `${import.meta.env.BASE_URL}login`
        throw err
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

// ── Base HTTP client ──────────────────────────────────────────────────────────

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {
    // Skip Content-Type for FormData — let the browser set the multipart boundary automatically.
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }

  if (store.accessToken) {
    headers['Authorization'] = `Bearer ${store.accessToken}`
  }

  const response = await fetch(`${BASE}${endpoint}`, { ...options, headers })

  if (response.status === 401) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` }
    const retry = await fetch(`${BASE}${endpoint}`, { ...options, headers: retryHeaders })

    if (!retry.ok) {
      const body = await retry.json().catch(() => ({}))
      throw new Error(
        `[${retry.status}] ${(body as { error?: { message?: string } })?.error?.message ?? retry.statusText}`
      )
    }
    if (retry.status === 204) return undefined as T
    return retry.json() as Promise<T>
  }

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = await response.json()
      message = body?.error?.message ?? message
    } catch {
      // ignore parse errors
    }
    throw new Error(`[${response.status}] ${message}`)
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

function buildQuery(params?: Record<string, string | boolean | number | undefined>): string {
  if (!params) return ''
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')
  return q ? `?${q}` : ''
}

// ── Raw binary fetch (for chunked upload — no JSON body, needs 401 retry) ─────

async function rawFetch(
  endpoint: string,
  method: string,
  headers: Record<string, string>,
  body?: ArrayBuffer
): Promise<Response> {
  const store = useAuthStore.getState()
  const allHeaders: Record<string, string> = { ...headers }
  if (store.accessToken) allHeaders['Authorization'] = `Bearer ${store.accessToken}`

  const response = await fetch(`${BASE}${endpoint}`, { method, headers: allHeaders, body })

  if (response.status === 401) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...allHeaders, Authorization: `Bearer ${freshToken}` }
    return fetch(`${BASE}${endpoint}`, { method, headers: retryHeaders, body })
  }

  return response
}

// ── Sources API ───────────────────────────────────────────────────────────────

export const sourcesApi = {
  list(orgId: string, params?: ListSourcesParams): Promise<ApiResponse<Source[]>> {
    const q = buildQuery(params as Record<string, string | undefined>)
    return request(`/api/v1/organizations/${orgId}/sources${q}`)
  },

  get(orgId: string, id: string): Promise<ApiResponse<Source>> {
    return request(`/api/v1/organizations/${orgId}/sources/${id}`)
  },

  create(orgId: string, body: CreateSourceRequest): Promise<ApiResponse<Source>> {
    return request(`/api/v1/organizations/${orgId}/sources`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  update(orgId: string, id: string, body: UpdateSourceRequest): Promise<ApiResponse<Source>> {
    return request(`/api/v1/organizations/${orgId}/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },

  delete(orgId: string, id: string): Promise<void> {
    return request(`/api/v1/organizations/${orgId}/sources/${id}`, { method: 'DELETE' })
  },

  setCredential(
    orgId: string,
    id: string,
    body: SetCredentialRequest
  ): Promise<ApiResponse<SourceCredential>> {
    return request(`/api/v1/organizations/${orgId}/sources/${id}/credential`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },

  deleteCredential(orgId: string, id: string): Promise<void> {
    return request(`/api/v1/organizations/${orgId}/sources/${id}/credential`, {
      method: 'DELETE',
    })
  },

  upload(
    orgId: string,
    id: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<ScanJob>> {
    return new Promise((resolve, reject) => {
      const form = new FormData()
      form.append('archive', file)
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${BASE}/api/v1/organizations/${orgId}/sources/${id}/upload`)
      const token = useAuthStore.getState().accessToken
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }
      xhr.onload = () => {
        const ok = xhr.status >= 200 && xhr.status < 300
        try {
          const body = JSON.parse(xhr.responseText)
          if (ok) resolve(body)
          else reject(new Error(`[${xhr.status}] ${body?.error?.message ?? xhr.statusText}`))
        } catch {
          if (ok) resolve(undefined as unknown as ApiResponse<ScanJob>)
          else reject(new Error(`[${xhr.status}] ${xhr.statusText}`))
        }
      }
      xhr.onerror = () => reject(new Error('Network error during upload'))
      xhr.send(form)
    })
  },

  // ── Resumable upload session API ──────────────────────────────────────────

  async createUploadSession(
    orgId: string,
    id: string,
    totalSize: number,
    filename: string
  ): Promise<UploadSessionResponse> {
    const res = await rawFetch(
      `/api/v1/organizations/${orgId}/sources/${id}/upload/sessions`,
      'POST',
      { 'Upload-Length': String(totalSize), 'Upload-Name': filename }
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(`[${res.status}] ${(body as { error?: { message?: string } })?.error?.message ?? res.statusText}`)
    }
    return res.json() as Promise<UploadSessionResponse>
  },

  async getUploadSessionOffset(
    orgId: string,
    id: string,
    sessionId: string
  ): Promise<number> {
    const res = await rawFetch(
      `/api/v1/organizations/${orgId}/sources/${id}/upload/sessions/${sessionId}`,
      'HEAD',
      {}
    )
    if (!res.ok) {
      throw new Error(`[${res.status}] Failed to query upload session`)
    }
    return parseInt(res.headers.get('Upload-Offset') ?? '0', 10)
  },

  async uploadChunk(
    orgId: string,
    id: string,
    sessionId: string,
    chunk: ArrayBuffer,
    offset: number
  ): Promise<{ done: boolean; newOffset: number }> {
    const res = await rawFetch(
      `/api/v1/organizations/${orgId}/sources/${id}/upload/sessions/${sessionId}`,
      'PATCH',
      {
        'Upload-Offset': String(offset),
        'Content-Type': 'application/offset+octet-stream',
        'Content-Length': String(chunk.byteLength),
      },
      chunk
    )

    if (res.status === 409) {
      const body = await res.json().catch(() => ({})) as { current_offset?: number }
      throw new UploadOffsetMismatchError(body.current_offset ?? offset)
    }

    if (res.status === 413) {
      throw new UploadChunkTooLargeError(chunk.byteLength)
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(`[${res.status}] ${(body as { error?: { message?: string } })?.error?.message ?? res.statusText}`)
    }

    const newOffset = offset + chunk.byteLength
    return { done: res.status === 200, newOffset }
  },

  async cancelUploadSession(orgId: string, id: string, sessionId: string): Promise<void> {
    await rawFetch(
      `/api/v1/organizations/${orgId}/sources/${id}/upload/sessions/${sessionId}`,
      'DELETE',
      {}
    )
  },
}

// ── Scans API ─────────────────────────────────────────────────────────────────

export const scansApi = {
  list(orgId: string, params?: ListScansParams): Promise<ApiResponse<ScanJob[]>> {
    const q = buildQuery(params as Record<string, string | undefined>)
    return request(`/api/v1/organizations/${orgId}/scans${q}`)
  },

  get(orgId: string, id: string): Promise<ApiResponse<ScanJob>> {
    return request(`/api/v1/organizations/${orgId}/scans/${id}`)
  },

  create(orgId: string, body: CreateScanRequest): Promise<ApiResponse<ScanJob>> {
    return request(`/api/v1/organizations/${orgId}/scans`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  cancel(orgId: string, id: string): Promise<void> {
    return request(`/api/v1/organizations/${orgId}/scans/${id}`, { method: 'DELETE' })
  },
}

// ── CBOM / Reports API ────────────────────────────────────────────────────────

export const cbomApi = {
  async downloadOrgCbom(orgId: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/cbom`,
      `cbom-${orgId}.json`,
      'application/vnd.cyclonedx+json'
    )
  },

  async downloadOrgFindings(orgId: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/cbom/findings`,
      `findings-${orgId}.json`
    )
  },

  async downloadOrgSarif(orgId: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/cbom/sarif`,
      `sarif-${orgId}.json`
    )
  },

  async downloadScanCbom(orgId: string, scanId: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/scans/${scanId}/cbom`,
      `cbom-scan-${scanId}.json`,
      'application/vnd.cyclonedx+json'
    )
  },

  async downloadScanFindings(orgId: string, scanId: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/scans/${scanId}/cbom/findings`,
      `findings-scan-${scanId}.json`
    )
  },

  async downloadScanSarif(orgId: string, scanId: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/scans/${scanId}/cbom/sarif`,
      `sarif-scan-${scanId}.json`
    )
  },

  importBom(orgId: string, sourceId: string, body: BomImportRequest): Promise<BomImportResult> {
    return request(`/api/v1/organizations/${orgId}/sources/${sourceId}/bom/import`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async downloadSourceCbom(orgId: string, sourceId: string, sourceName?: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/sources/${sourceId}/cbom`,
      `cbom-${sourceName ?? sourceId}.json`,
      'application/vnd.cyclonedx+json'
    )
  },

  async downloadSourceFindings(orgId: string, sourceId: string, sourceName?: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/sources/${sourceId}/cbom/findings`,
      `findings-${sourceName ?? sourceId}.json`
    )
  },

  async downloadSourceSarif(orgId: string, sourceId: string, sourceName?: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/sources/${sourceId}/cbom/sarif`,
      `sarif-${sourceName ?? sourceId}.json`
    )
  },

  // Fetch findings as parsed JSON (not a file download).
  // The findings endpoint returns the report object directly — no {success,data} wrapper.
  fetchOrgFindings(orgId: string, params?: ListFindingsParams): Promise<FindingsReport> {
    const q = buildQuery(params as Record<string, string | undefined>)
    return request(`/api/v1/organizations/${orgId}/cbom/findings${q}`)
  },

  fetchScanFindings(orgId: string, scanId: string, params?: ListFindingsParams): Promise<FindingsReport> {
    const q = buildQuery(params as Record<string, string | undefined>)
    return request(`/api/v1/organizations/${orgId}/scans/${scanId}/cbom/findings${q}`)
  },

  fetchScanPackageFindings(orgId: string, scanId: string, params?: ListPackageFindingsParams): Promise<PackageFindingsReport> {
    const q = buildQuery(params as Record<string, string | boolean | number | undefined>)
    return request(`/api/v1/organizations/${orgId}/scans/${scanId}/sbom/findings${q}`)
  },

  fetchSourceBomFindings(orgId: string, sourceId: string): Promise<BOMFindingsReport> {
    return request(`/api/v1/organizations/${orgId}/sources/${sourceId}/bom/findings`)
  },

  fetchSourcePackageFindings(orgId: string, sourceId: string): Promise<PackageFindingsReport> {
    return request(`/api/v1/organizations/${orgId}/sources/${sourceId}/sbom/findings`)
  },

  fetchOrgPackageFindings(orgId: string, params?: ListPackageFindingsParams): Promise<PackageFindingsReport> {
    const q = buildQuery(params as Record<string, string | boolean | number | undefined>)
    return request(`/api/v1/organizations/${orgId}/sbom/findings${q}`)
  },

  getCbomVersions(orgId: string, sourceId: string): Promise<CbomVersionsResponse> {
    return request(`/api/v1/organizations/${orgId}/sources/${sourceId}/cbom/versions`)
  },
}

// ── Scanners API ──────────────────────────────────────────────────────────────

export const scannersApi = {
  list(): Promise<ApiResponse<ScannerInfo[]>> {
    return request('/api/v1/scanners')
  },
}


// ── SBOM API ──────────────────────────────────────────────────────────────────

export const sbomApi = {
  listComponents(
    orgId: string,
    sourceId: string,
    params?: ListSbomComponentsParams
  ): Promise<SbomComponentsResponse> {
    const q = buildQuery(params as Record<string, string | boolean | number | undefined>)
    return request(`/api/v1/organizations/${orgId}/sources/${sourceId}/sbom/components${q}`)
  },

  async downloadSourceSbom(
    orgId: string,
    sourceId: string,
    format: 'cyclonedx' | 'spdx' | 'swid',
    sourceName?: string
  ): Promise<void> {
    const ext = format === 'swid' ? 'xml' : 'json'
    const filename = `sbom-${sourceName ?? sourceId}-${format}.${ext}`
    const accept =
      format === 'cyclonedx'
        ? 'application/vnd.cyclonedx+json; version=1.7'
        : format === 'swid'
        ? 'application/swid-tag+xml'
        : 'application/json'
    await triggerDownload(
      `/api/v1/organizations/${orgId}/sources/${sourceId}/sbom?format=${format}`,
      filename,
      accept
    )
  },

  async downloadScanSbom(
    orgId: string,
    scanJobId: string,
    format: 'cyclonedx' | 'spdx' | 'swid'
  ): Promise<void> {
    const ext = format === 'swid' ? 'xml' : 'json'
    await triggerDownload(
      `/api/v1/organizations/${orgId}/scans/${scanJobId}/sbom?format=${format}`,
      `sbom-scan-${scanJobId}-${format}.${ext}`
    )
  },

  getSbomVersions(orgId: string, sourceId: string): Promise<SbomVersionsResponse> {
    return request(`/api/v1/organizations/${orgId}/sources/${sourceId}/sbom/versions`)
  },

  async downloadOrgSbom(orgId: string, format: 'cyclonedx' | 'spdx' | 'swid'): Promise<void> {
    const ext = format === 'swid' ? 'xml' : 'json'
    const accept =
      format === 'cyclonedx'
        ? 'application/vnd.cyclonedx+json; version=1.7'
        : format === 'swid'
        ? 'application/swid-tag+xml'
        : 'application/json'
    await triggerDownload(
      `/api/v1/organizations/${orgId}/sbom?format=${format}`,
      `sbom-org-${orgId}-${format}.${ext}`,
      accept
    )
  },
}

// ── Inventory API ─────────────────────────────────────────────────────────────

export const inventoryApi = {
  list(orgId: string, params?: { page?: number; page_size?: number }): Promise<InventoriesPage> {
    const q = buildQuery(params as Record<string, number | undefined>)
    return request(`/api/v1/organizations/${orgId}/inventories${q}`)
  },

  get(orgId: string, id: string): Promise<InventoryDetail> {
    return request(`/api/v1/organizations/${orgId}/inventories/${id}`)
  },

  create(orgId: string, body: CreateInventoryRequest): Promise<Inventory> {
    return request(`/api/v1/organizations/${orgId}/inventories`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  update(orgId: string, id: string, body: UpdateInventoryRequest): Promise<Inventory> {
    return request(`/api/v1/organizations/${orgId}/inventories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },

  delete(orgId: string, id: string): Promise<void> {
    return request(`/api/v1/organizations/${orgId}/inventories/${id}`, { method: 'DELETE' })
  },

  async addSources(orgId: string, id: string, body: AddSourcesRequest): Promise<AddSourcesResult> {
    const store = useAuthStore.getState()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`

    const res = await fetch(`${BASE}/api/v1/organizations/${orgId}/inventories/${id}/sources`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    // 409 = all sources already members — still a valid result, not an error
    if (res.status === 200 || res.status === 409) return res.json() as Promise<AddSourcesResult>

    let message = res.statusText
    try {
      const b = await res.json()
      message = b?.error?.message ?? message
    } catch { /* ignore */ }
    throw new Error(`[${res.status}] ${message}`)
  },

  removeSource(orgId: string, id: string, sourceId: string): Promise<void> {
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/sources/${sourceId}`, {
      method: 'DELETE',
    })
  },

  async downloadCompositeSbom(
    orgId: string,
    id: string,
    format: 'cyclonedx' | 'spdx' | 'swid',
    inventoryName?: string
  ): Promise<void> {
    const ext = format === 'swid' ? 'xml' : 'json'
    const filename = `composite-sbom-${inventoryName ?? id}-${format}.${ext}`
    const accept =
      format === 'cyclonedx'
        ? 'application/vnd.cyclonedx+json; version=1.7'
        : format === 'swid'
        ? 'application/swid-tag+xml'
        : 'application/json'
    await triggerDownload(
      `/api/v1/organizations/${orgId}/inventories/${id}/sbom?format=${format}`,
      filename,
      accept
    )
  },

  async downloadCompositeCbom(orgId: string, id: string, inventoryName?: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/inventories/${id}/cbom`,
      `cbom-${inventoryName ?? id}.json`,
      'application/vnd.cyclonedx+json'
    )
  },

  async downloadCompositeFindings(orgId: string, id: string, inventoryName?: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/inventories/${id}/cbom/findings`,
      `findings-${inventoryName ?? id}.json`
    )
  },

  async downloadCompositeSarif(orgId: string, id: string, inventoryName?: string): Promise<void> {
    await triggerDownload(
      `/api/v1/organizations/${orgId}/inventories/${id}/cbom/sarif`,
      `sarif-${inventoryName ?? id}.json`
    )
  },

  getRisk(orgId: string, id: string): Promise<InventoryRisk> {
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/risk`)
  },

  getRiskHistory(orgId: string, id: string, days?: number): Promise<RiskHistory> {
    const q = days ? `?days=${days}` : ''
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/risk/history${q}`)
  },

  getSourceRisk(orgId: string, id: string, sourceId: string): Promise<SourceRiskDetail> {
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/sources/${sourceId}/risk`)
  },

  fetchPackageFindings(orgId: string, id: string, params?: ListPackageFindingsParams): Promise<PackageFindingsReport> {
    const q = buildQuery(params as Record<string, string | undefined>)
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/sbom/findings${q}`)
  },

  fetchFindings(orgId: string, id: string, params?: ListFindingsParams): Promise<FindingsReport> {
    const q = buildQuery(params as Record<string, string | undefined>)
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/cbom/findings${q}`)
  },

  getInventoryCompositeSbomVersions(
    orgId: string,
    id: string,
    params?: { offset?: number; limit?: number }
  ): Promise<InventoryCompositeSbomVersionsResponse> {
    const q = buildQuery({ order: 'desc', ...params } as Record<string, string | number | undefined>)
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/sbom/versions${q}`)
  },

  getInventoryCompositeCbomVersions(
    orgId: string,
    id: string,
    params?: { offset?: number; limit?: number }
  ): Promise<InventoryCompositeCbomVersionsResponse> {
    const q = buildQuery({ order: 'desc', ...params } as Record<string, string | number | undefined>)
    return request(`/api/v1/organizations/${orgId}/inventories/${id}/cbom/versions${q}`)
  },

  async downloadInventoryCompositeCbomVersion(
    orgId: string,
    id: string,
    snapshotId: string,
    format: 'cyclonedx' | 'findings' | 'sarif',
    inventoryName?: string
  ): Promise<void> {
    const ext = format === 'sarif' ? 'sarif' : 'json'
    const filename = `composite-cbom-${inventoryName ?? id}-${snapshotId.slice(0, 8)}-${format}.${ext}`
    await triggerDownload(
      `/api/v1/organizations/${orgId}/inventories/${id}/cbom/versions/${snapshotId}?format=${format}`,
      filename
    )
  },

  async downloadInventoryCompositeSbomVersion(
    orgId: string,
    id: string,
    snapshotId: string,
    format: 'cyclonedx' | 'spdx' | 'swid',
    inventoryName?: string
  ): Promise<void> {
    const ext = format === 'swid' ? 'xml' : 'json'
    const filename = `composite-sbom-${inventoryName ?? id}-${snapshotId.slice(0, 8)}-${format}.${ext}`
    const accept =
      format === 'cyclonedx'
        ? 'application/vnd.cyclonedx+json; version=1.7'
        : format === 'swid'
        ? 'application/swid-tag+xml'
        : 'application/json'
    await triggerDownload(
      `/api/v1/organizations/${orgId}/inventories/${id}/sbom/versions/${snapshotId}?format=${format}`,
      filename,
      accept
    )
  },
}

// ── CMDB API ──────────────────────────────────────────────────────────────────

const cmdbBase = (orgId: string) => `/api/v1/organizations/${orgId}/cmdb`
const connBase = (orgId: string, cid: string) => `${cmdbBase(orgId)}/handlers/${cid}`

export const cmdbApi = {
  // Handlers
  listHandlers(orgId: string): Promise<CmdbHandler[]> {
    return request(`${cmdbBase(orgId)}/handlers`)
  },
  getHandler(orgId: string, cid: string): Promise<CmdbHandler> {
    return request(connBase(orgId, cid))
  },
  createHandler(orgId: string, body: CreateHandlerRequest): Promise<CmdbHandler> {
    return request(`${cmdbBase(orgId)}/handlers`, { method: 'POST', body: JSON.stringify(body) })
  },
  updateHandler(orgId: string, cid: string, body: UpdateHandlerRequest): Promise<CmdbHandler> {
    return request(connBase(orgId, cid), { method: 'PUT', body: JSON.stringify(body) })
  },
  deleteHandler(orgId: string, cid: string): Promise<void> {
    return request(connBase(orgId, cid), { method: 'DELETE' })
  },
  activateHandler(orgId: string, cid: string): Promise<CmdbHandler> {
    return request(`${connBase(orgId, cid)}/activate`, { method: 'POST' })
  },
  deactivateHandler(orgId: string, cid: string): Promise<CmdbHandler> {
    return request(`${connBase(orgId, cid)}/deactivate`, { method: 'POST' })
  },

  // Health
  healthCheck(orgId: string, cid: string): Promise<CmdbHealthResult> {
    return request(`${connBase(orgId, cid)}/health`)
  },

  // Field mappings
  getFieldMappings(orgId: string, cid: string): Promise<CmdbFieldMapping[]> {
    return request(`${connBase(orgId, cid)}/field-mapping`)
  },
  replaceFieldMappings(orgId: string, cid: string, body: CmdbFieldMapping[]): Promise<void> {
    return request(`${connBase(orgId, cid)}/field-mapping`, { method: 'PUT', body: JSON.stringify(body) })
  },

  // Sync
  triggerFullSync(orgId: string, cid: string): Promise<void> {
    return request(`${connBase(orgId, cid)}/sync`, { method: 'POST' })
  },
  triggerInventorySync(orgId: string, cid: string, inventoryId: string): Promise<void> {
    return request(`${connBase(orgId, cid)}/sync/${inventoryId}`, { method: 'POST' })
  },
  getSyncStatus(orgId: string, cid: string, params?: ListSyncStatusParams): Promise<CmdbSyncStatusPage> {
    const q = buildQuery(params as Record<string, string | boolean | number | undefined>)
    return request(`${connBase(orgId, cid)}/sync-status${q}`)
  },

  // Risk summary
  getRiskSummary(orgId: string, cid: string): Promise<CmdbRiskSummary> {
    return request(`${connBase(orgId, cid)}/risk-summary`)
  },

  // Match wizard
  getMatchSuggestions(orgId: string, cid: string): Promise<CmdbMatchSuggestion[]> {
    return request(`${connBase(orgId, cid)}/match-suggestions`)
  },
  confirmMatches(orgId: string, cid: string, body: ConfirmMatchesRequest): Promise<void> {
    return request(`${connBase(orgId, cid)}/match-confirm`, { method: 'POST', body: JSON.stringify(body) })
  },

  // Credentials
  setCredential(orgId: string, cid: string, body: CmdbSetCredentialRequest): Promise<void> {
    return request(`${connBase(orgId, cid)}/credential`, { method: 'PUT', body: JSON.stringify(body) })
  },
  getCredentialHint(orgId: string, cid: string): Promise<CredentialHintResponse> {
    return request(`${connBase(orgId, cid)}/credential`)
  },

  // Adapter templates
  listAdapterTemplates(orgId: string): Promise<AdapterTemplate[]> {
    return request(`${cmdbBase(orgId)}/adapter-templates`)
  },

  // Field discovery
  discoverFields(orgId: string, cid: string): Promise<FieldDiscoveryResult> {
    return request(`${connBase(orgId, cid)}/discover-fields`)
  },

  // Provision guide (Update Set XML for ServiceNow API adapters)
  getHandlerProvisionGuide(orgId: string, cid: string): Promise<CmdbProvisionGuide> {
    return request(`${connBase(orgId, cid)}/provision-guide`)
  },

  // Standalone (org-level) import/export — no handler required
  standaloneImportJson(orgId: string, body: unknown): Promise<CmdbImportResult> {
    return request(`${cmdbBase(orgId)}/import`, { method: 'POST', body: JSON.stringify(body) })
  },
  standaloneImportFile(orgId: string, file: File, mappingProfileId?: string): Promise<CmdbImportResult> {
    const form = new FormData()
    form.append('file', file)
    if (mappingProfileId) form.append('mapping_profile_id', mappingProfileId)
    form.append('import_mode', 'upsert')
    return request(`${cmdbBase(orgId)}/import/file`, { method: 'POST', body: form })
  },
  async standaloneDownloadExport(orgId: string, format: FileFormat, profileId?: string): Promise<void> {
    const q = buildQuery({ format, ...(profileId ? { profile_id: profileId } : {}) })
    await triggerDownload(`${cmdbBase(orgId)}/export/download${q}`, `cmdb_export.${format}`)
  },

  // Mapping profiles (unified)
  listProfiles(orgId: string, direction?: ProfileDirection): Promise<CmdbMappingProfile[]> {
    const q = direction ? `?direction=${direction}` : ''
    return request<CmdbMappingProfile[]>(`${cmdbBase(orgId)}/profiles${q}`)
  },
  getProfile(orgId: string, pid: string): Promise<CmdbMappingProfile> {
    return request(`${cmdbBase(orgId)}/profiles/${pid}`)
  },
  createProfile(orgId: string, body: CreateCmdbProfileRequest): Promise<CmdbMappingProfile> {
    return request(`${cmdbBase(orgId)}/profiles`, { method: 'POST', body: JSON.stringify(body) })
  },
  updateProfile(orgId: string, pid: string, body: UpdateCmdbProfileRequest): Promise<CmdbMappingProfile> {
    return request(`${cmdbBase(orgId)}/profiles/${pid}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  deleteProfile(orgId: string, pid: string): Promise<void> {
    return request(`${cmdbBase(orgId)}/profiles/${pid}`, { method: 'DELETE' })
  },
}

// ── Credentials ───────────────────────────────────────────────────────────────

export const credentialsApi = {
  list(orgId: string): Promise<Credential[]> {
    return request(`/api/v1/organizations/${orgId}/credentials`)
  },
  create(orgId: string, body: CreateCredentialRequest): Promise<Credential> {
    return request(`/api/v1/organizations/${orgId}/credentials`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
  update(orgId: string, credId: string, body: UpdateCredentialRequest): Promise<Credential> {
    return request(`/api/v1/organizations/${orgId}/credentials/${credId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },
  delete(orgId: string, credId: string): Promise<void> {
    return request(`/api/v1/organizations/${orgId}/credentials/${credId}`, { method: 'DELETE' })
  },
}

// ── Download helper ───────────────────────────────────────────────────────────

async function triggerDownload(url: string, filename: string, accept?: string): Promise<void> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {}
  if (accept) headers['Accept'] = accept
  if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`

  const response = await fetch(`${BASE}${url}`, { headers })
  if (!response.ok) throw new Error(`Download failed: ${response.statusText}`)

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)
}
